import React, { memo, Fragment, useEffect, useState, useRef, useContext, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { gql, useQuery, useMutation } from '@apollo/client'
import { GuideContext } from '../../context/guide-context'
import DataTable from '../../components/common/DataTable'
import Notification from '../../components/partials/Notification'
import Card from '../../components/common/Card'
import { showNotification } from '../../utils/notifications'
import { CubeLoader } from '@/components/ui/cube-loader'
import Swal from 'sweetalert2'
import _ from 'lodash'
import { editIconSvg, gridPlusIconSvg, ListIcon, trashIconSvg, volumeIconSvg } from '@/components/icons/new'
import { dashboardIconSvg } from '@/components/icons/new/DashbordIcon'
import { audiencesIconSvg } from '@/components/icons/new/AudiencesIcon'
import { reportsIconSvg } from '@/components/icons/new/ReportsIcon'
import { communitiesIconSvg } from '@/components/icons/new/CommunitiesIcon'
import { useActionLock } from '@/hooks/useActionLock'
import { sortCampaignsByOrder } from './campaignListOrder'
import { buildOrderUpdates } from '@/utils/reorder'

const GET_CAMPAIGNS = gql`
    query Campaigns {
        campaigns {
            id
            name
            userStatus
            systemStatus
            hasSubscriptions
            isMediaBuyingLive
            createdAt
            order
            landingPagesAggregate {
                count
            }
            platformsAggregate {
                count
            }
            isMediaBuyingReady
        }
    }
`

const UPDATE_CAMPAIGN_ORDER = gql`
    mutation UpdateCampaignOrder($updates: [CampaignOrderInput!]!) {
        updateCampaignOrder(updates: $updates) {
            id
            order
        }
    }
`

const PAY_INVOICE = gql`
    mutation PayForCampaign($campaignId: ID!) {
        payForCampaign(campaignId: $campaignId) {
            id
            systemStatus
        }
    }
`

const DELETE_DRAFT_CAMPAIGN = gql`
    mutation DeleteDraftCampaign($campaignId: ID!) {
        deleteDraftCampaign(campaignId: $campaignId)
    }
`

const DELETE_PENDING_CAMPAIGN = gql`
    mutation DeletePendingCampaign($campaignId: ID!) {
        deletePendingCampaign(campaignId: $campaignId)
    }
`

const getCampaignRowId = (row) => row?.id?.id

const CampaignList = memo(() => {
    const { guide } = useContext(GuideContext)
    const { data, loading, refetch } = useQuery(GET_CAMPAIGNS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true, // This will update loading state during refetch
        onCompleted: (data) => {
            setNotificationShow(true)
            let paid = true
            let result = false
            let foundDrafts = false
            data?.campaigns.forEach((campaign) => {
                if (campaign.systemStatus === 'DRAFT') {
                    foundDrafts = true
                }

                if (campaign.userStatus === 'ACTIVE' && campaign.systemStatus !== 'DRAFT') {
                    if (campaign.landingPagesAggregate.count === 0 || campaign.platformsAggregate.count < 3) {
                        result = true
                    }
                }
                if (campaign.systemStatus !== 'ACTIVE' && campaign.systemStatus !== 'DRAFT') {
                    paid = false
                }
            })
            setHasIncompleteCampaigns(result)
            setPaidCampaigns(paid)
            setHasDrafts(foundDrafts)

            // Check if campaigns are ready for media buying
            let ready = []
            data?.campaigns.forEach((campaign) => {
                ready.push({
                    id: campaign.id,
                    status: true,
                    live: campaign.isMediaBuyingLive,
                })
            })
            setReadyForMediaBuying(ready)
        },
    })
    const [campaignData, setCampaignData] = useState([])
    const [hasIncompleteCampaigns, setHasIncompleteCampaigns] = useState(null)
    const [paidCampaigns, setPaidCampaigns] = useState(null)
    const [notificationShow, setNotificationShow] = useState(true)
    const [readyForMediaBuying, setReadyForMediaBuying] = useState(null)
    const navigate = useNavigate()
    const location = useLocation()

    const [hasDrafts, setHasDrafts] = useState(false)

    const guidedRef = useRef(false)
    const guidedRefPaid = useRef(false)
    const payLock = useActionLock()

    const [updateCampaignOrder] = useMutation(UPDATE_CAMPAIGN_ORDER, {
        onError({ graphQLErrors }) {
            showNotification(graphQLErrors[0]?.message || 'Failed to save the new order', 'error')
            refetch()
        },
    })

    const [payInvoice, payment] = useMutation(PAY_INVOICE, {
        onError({ graphQLErrors }) {
            showNotification(graphQLErrors[0]?.message, 'error')
        },
        onCompleted: (data) => {
            showNotification('Your payment was successfully processed. Thank you!', 'success')
            if (data.payForCampaign.systemStatus === 'ACTIVE') {
                setPaidCampaigns(true)
            }
            guidedRef.current = null
        },
    })

    const [deleteDraft] = useMutation(DELETE_DRAFT_CAMPAIGN, {
        onError({ graphQLErrors }) {
            showNotification(graphQLErrors[0]?.message, 'error')
        },
        onCompleted: () => {
            showNotification('Draft campaign has been deleted successfully', 'success')
            refetch()
        },
    })

    const [deletePending] = useMutation(DELETE_PENDING_CAMPAIGN, {
        onError({ graphQLErrors }) {
            showNotification(graphQLErrors[0]?.message, 'error')
        },
        onCompleted: () => {
            showNotification('Pending license has been deleted successfully', 'success')
            refetch()
        },
    })

    const columns = [
        {
            title: 'Name',
            data: 'name',
            render: (name) => `<span class="small">${name}</span>`,
        },
        {
            title: 'Status',
            data: 'status',
            render: (data) => {
                return `<span class="small badge rounded-pill ${
                    data.status === 'Active' ? 'bg-success' : 'bg-danger'
                }">${data.status}</span>`
            },
        },
        {
            title: 'Billing',
            data: 'billing',
            render: (data) => {
                if (data.paid || data.isDraft) {
                    return data.isDraft
                        ? '<span class="badge rounded-pill bg-danger small">Draft</span>'
                        : '<span class="badge rounded-pill bg-success small">Paid</span>'
                }

                // Licences billed through subscription plans are settled from their subscription,
                // not by this button, which only understands the older single-invoice billing.
                if (data.hasSubscriptions) {
                    return (
                        '<div class="flex align-items-center list-campaign-action"><a class="btn btn-sm btn-primary complete-payment-icon" data-target="' +
                        data.id +
                        '">Complete Payment</a></div>'
                    )
                }

                return (
                    '<div class="flex align-items-center list-campaign-action"><a class="btn btn-sm btn-primary pay-icon" data-target="' +
                    data.id +
                    '">Pay Invoice</a></div>'
                )
            },
        },
        {
            title: 'Created',
            data: 'date',
            render: (date) => `<span class="small">${date}</span>`,
        },
        // {
        //     title: 'Media Buying Status',
        //     data: 'isMediaBuyingLive',
        //     render: (data) => {
        //         if (data.isDraft) {
        //             return '<span class="badge bg-warning">Draft</span>'
        //         }
        //         return (
        //             '<span class="badge ' +
        //             (data.status ? 'bg-success' : 'bg-danger') +
        //             '">' +
        //             (data.status ? 'Active' : 'Paused') +
        //             '</span>'
        //         )
        //     },
        // },
        {
            title: 'Action',
            data: 'id',
            className: 'text-end',
            orderable: false,
            render: (data) => {
                if (Array.isArray(readyForMediaBuying)) {
                    const isDraft = data.isDraft
                    const status = readyForMediaBuying.find((element) => element.id === data.id)

                    // For draft campaigns, only show edit and delete options
                    if (isDraft) {
                        return (
                            '<div class="flex align-items-center list-campaign-action">' +
                            '<a class="btn btn-icon edit-icon opacity-100 tooltip" data-target="' +
                            data.id +
                            '"><span class="btn-inner">' +
                            editIconSvg +
                            '</span><span class="tooltiptext">Edit Draft</span></a> ' +
                            '<a class="btn btn-sm btn-primary complete-draft-icon" data-target="' +
                            data.id +
                            '">Complete Setup</a> ' +
                            '<a class="btn btn-icon delete-icon opacity-100 tooltip" data-target="' +
                            data.id +
                            '"><span class="btn-inner">' +
                            trashIconSvg +
                            '</span><span class="tooltiptext">Delete Draft</span></a>' +
                            '</div>'
                        )
                    }

                    // For regular campaigns
                    const media = status?.status
                        ? '<a class="btn btn-icon media-buying-icon opacity-100 tooltip" data-status="' +
                          status.status +
                          '" data-target="' +
                          data.id +
                          '"><span class="btn-inner">' +
                          volumeIconSvg +
                          '</span><span class="tooltiptext">Start Media Buying</span></a>'
                        : ''
                    const platform =
                        data.status === 'ACTIVE'
                            ? '<a class="btn btn-icon social-icon opacity-100 tooltip" data-target="' +
                              data.id +
                              '"><span class="btn-inner">' +
                              gridPlusIconSvg +
                              '</span><span class="tooltiptext">Platform Settings</span></a> '
                            : ''
                    const dashboard =
                        '<a class="btn btn-icon dashboard-icon opacity-100 tooltip" data-target="' +
                        data.id +
                        '"><span class="btn-inner">' +
                        dashboardIconSvg +
                        '</span><span class="tooltiptext">Dashboard</span></a> '
                    const audiences =
                        '<a class="btn btn-icon audiences-icon opacity-100 tooltip" data-target="' +
                        data.id +
                        '"><span class="btn-inner">' +
                        audiencesIconSvg +
                        '</span><span class="tooltiptext">Audiences</span></a> '
                    const reports =
                        '<a class="btn btn-icon reports-icon opacity-100 tooltip" data-target="' +
                        data.id +
                        '"><span class="btn-inner">' +
                        reportsIconSvg +
                        '</span><span class="tooltiptext">AI Reports</span></a> '
                    const communities =
                        '<a class="btn btn-icon communities-icon opacity-100 tooltip" data-target="' +
                        data.id +
                        '"><span class="btn-inner">' +
                        communitiesIconSvg +
                        '</span><span class="tooltiptext">Communities</span></a> '
                    const deletePending =
                        data.status === 'PENDING'
                            ? '<a class="btn btn-icon delete-pending-icon opacity-100 tooltip" data-target="' +
                              data.id +
                              '"><span class="btn-inner">' +
                              trashIconSvg +
                              '</span><span class="tooltiptext">Delete Pending License</span></a> '
                            : ''
                    return (
                        '<div class="flex align-items-center list-campaign-action"> ' +
                        dashboard +
                        audiences +
                        reports +
                        communities +
                        '<a class="btn btn-icon edit-icon opacity-100 tooltip" data-target="' +
                        data.id +
                        '"><span class="btn-inner">' +
                        editIconSvg +
                        '</span><span class="tooltiptext">Edit License</span></a> ' +
                        platform +
                        media +
                        deletePending +
                        '</div>'
                    )
                }
                return data
            },
        },
    ]

    const getCampaignStatus = (campaign) => {
        if (campaign.systemStatus === 'DRAFT') {
            return { status: 'Draft', color: 'bg-warning' }
        }

        if (campaign.userStatus === 'ACTIVE') {
            if (campaign.systemStatus === 'ACTIVE') {
                return { status: 'Active', color: 'bg-primary' }
            } else {
                if (campaign.systemStatus === 'PENDING') {
                    return { status: 'Pending', color: 'bg-warning' }
                } else {
                    return { status: 'Suspended', color: 'bg-info' }
                }
            }
        } else {
            return { status: 'Inactive', color: 'bg-danger' }
        }
    }

    useEffect(() => {
        const array = []
        if (data) {
            _.each(data.campaigns, (item) => {
                const { status, color } = getCampaignStatus(item)
                const isDraft = item.systemStatus === 'DRAFT'
                array.push({
                    id: {
                        id: item.id,
                        status: item.systemStatus,
                        isDraft: isDraft,
                    },
                    name: item.name || 'Untitled Campaign',
                    status: {
                        status: status,
                        color: color,
                    },
                    billing: {
                        paid: item.systemStatus === 'ACTIVE',
                        id: item.id,
                        isDraft: isDraft,
                        hasSubscriptions: item.hasSubscriptions,
                    },
                    date: new Date(Date.parse(item.createdAt)).toLocaleString('en-GB'),
                    isMediaBuyingLive: {
                        status: item.isMediaBuyingLive,
                        isDraft: isDraft,
                    },
                    creatives: isDraft ? 'N/A' : item.landingPagesAggregate.count,
                    order: item.order,
                })
            })

            if (array) {
                setCampaignData(sortCampaignsByOrder(array))
            }
        }
    }, [data])

    const handleCampaignReorder = useCallback(
        ({ orderedIds }) => {
            if (!Array.isArray(orderedIds) || orderedIds.length === 0) {
                return
            }

            const campaignById = new Map(campaignData.map((campaign) => [campaign.id.id, campaign]))
            const reorderedCampaigns = orderedIds.map((id) => campaignById.get(id)).filter(Boolean)
            const reorderedSet = new Set(reorderedCampaigns.map((campaign) => campaign.id.id))
            const remainingCampaigns = campaignData.filter((campaign) => !reorderedSet.has(campaign.id.id))
            const nextCampaigns = [...reorderedCampaigns, ...remainingCampaigns]

            setCampaignData(nextCampaigns)
            updateCampaignOrder({
                variables: { updates: buildOrderUpdates(nextCampaigns.map((campaign) => campaign.id.id)) },
            })
        },
        [campaignData, updateCampaignOrder],
    )

    const onClickDashboardButton = (event) => {
        const campaignId = event.currentTarget.getAttribute('data-target')
        navigate(`/campaigns/${campaignId}/dashboard`)
    }

    const onClickAudiencesButton = (event) => {
        const campaignId = event.currentTarget.getAttribute('data-target')
        navigate(`/campaigns/${campaignId}/social-feed`)
    }

    const onClickReportsButton = (event) => {
        const campaignId = event.currentTarget.getAttribute('data-target')
        navigate(`/campaigns/${campaignId}/reports`)
    }

    const onClickCommunitiesButton = (event) => {
        const campaignId = event.currentTarget.getAttribute('data-target')
        navigate(`/campaigns/${campaignId}/communities`)
    }

    const onClickEditButton = (event) => {
        const campaignId = event.currentTarget.getAttribute('data-target')
        navigate('/campaigns/edit/' + campaignId)
    }

    const onClickPlatformsButton = (event) => {
        const campaignId = event.currentTarget.getAttribute('data-target')
        navigate('/campaigns/platforms/' + campaignId)
    }

    const onClickLandingPageButton = (event) => {
        const campaignId = event.currentTarget.getAttribute('data-target')
        navigate(`/campaigns/${campaignId}/landing-pages/`)
    }

    const onClickMediaBuyingButton = (event) => {
        const campaignId = event.currentTarget.getAttribute('data-target')
        if (event.currentTarget.getAttribute('data-status') === 'true') {
            navigate(`/campaigns/media-buying/${campaignId}/ads-manager/`)
        }
    }

    // Settling a subscription happens on the subscriptions screen, which already knows the plans and
    // payment methods involved. Sending the customer there keeps one place responsible for payment.
    // A draft is finished in the subscription wizard, which is where a plan is chosen and paid for.
    const onClickCompleteDraftButton = (event) => {
        const campaignId = event.currentTarget.getAttribute('data-target')
        navigate(`/campaigns/new?draft=${encodeURIComponent(campaignId)}`)
    }

    const onClickCompletePaymentButton = (event) => {
        const campaignId = event.currentTarget.getAttribute('data-target')
        navigate(`/subscriptions?license=${encodeURIComponent(campaignId)}`)
    }

    const onClickPayButton = (event) => {
        const campaignId = event.currentTarget.getAttribute('data-target')
        return payLock(() =>
            payInvoice({
                variables: {
                    campaignId: campaignId,
                },
            }),
        )
    }

    const onClickDeleteButton = async (event) => {
        const campaignId = event.currentTarget.getAttribute('data-target')

        // Confirm deletion
        const result = await Swal.fire({
            title: 'Delete Draft Campaign',
            text: 'Are you sure you want to delete this draft campaign? This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it',
            cancelButtonText: 'No, keep it',
        })

        if (result.isConfirmed) {
            await deleteDraft({
                variables: {
                    campaignId: campaignId,
                },
            })
        }
    }

    const onClickDeletePendingButton = async (event) => {
        const campaignId = event.currentTarget.getAttribute('data-target')

        // Confirm deletion
        const result = await Swal.fire({
            title: 'Delete Pending License',
            text: 'Are you sure you want to delete this pending license? This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it',
            cancelButtonText: 'No, keep it',
        })

        if (result.isConfirmed) {
            await deletePending({
                variables: {
                    campaignId: campaignId,
                },
            })
        }
    }

    const bindButtonEvents = () => {
        const dashboardButtons = document.getElementsByClassName('dashboard-icon')
        const audiencesButtons = document.getElementsByClassName('audiences-icon')
        const reportsButtons = document.getElementsByClassName('reports-icon')
        const communitiesButtons = document.getElementsByClassName('communities-icon')
        const editButtons = document.getElementsByClassName('edit-icon')
        const platformButtons = document.getElementsByClassName('social-icon')
        const landingPageButtons = document.getElementsByClassName('settings-icon')
        const mediaBuyingButtons = document.getElementsByClassName('media-buying-icon')
        const payButtons = document.getElementsByClassName('pay-icon')
        const completePaymentButtons = document.getElementsByClassName('complete-payment-icon')
        const completeDraftButtons = document.getElementsByClassName('complete-draft-icon')
        const deleteButtons = document.getElementsByClassName('delete-icon')
        const deletePendingButtons = document.getElementsByClassName('delete-pending-icon')

        _.each(dashboardButtons, (element) => {
            element.removeEventListener('click', onClickDashboardButton)
            element.addEventListener('click', onClickDashboardButton)
        })
        _.each(audiencesButtons, (element) => {
            element.removeEventListener('click', onClickAudiencesButton)
            element.addEventListener('click', onClickAudiencesButton)
        })
        _.each(reportsButtons, (element) => {
            element.removeEventListener('click', onClickReportsButton)
            element.addEventListener('click', onClickReportsButton)
        })
        _.each(communitiesButtons, (element) => {
            element.removeEventListener('click', onClickCommunitiesButton)
            element.addEventListener('click', onClickCommunitiesButton)
        })
        _.each(editButtons, (element) => {
            element.removeEventListener('click', onClickEditButton)
            element.addEventListener('click', onClickEditButton)
        })
        _.each(platformButtons, (element) => {
            element.removeEventListener('click', onClickPlatformsButton)
            element.addEventListener('click', onClickPlatformsButton)
        })
        _.each(landingPageButtons, (element) => {
            element.removeEventListener('click', onClickLandingPageButton)
            element.addEventListener('click', onClickLandingPageButton)
        })
        _.each(mediaBuyingButtons, (element) => {
            element.removeEventListener('click', onClickMediaBuyingButton)
            element.addEventListener('click', onClickMediaBuyingButton)
        })
        _.each(completeDraftButtons, (element) => {
            element.removeEventListener('click', onClickCompleteDraftButton)
            element.addEventListener('click', onClickCompleteDraftButton)
        })
        _.each(completePaymentButtons, (element) => {
            element.removeEventListener('click', onClickCompletePaymentButton)
            element.addEventListener('click', onClickCompletePaymentButton)
        })
        _.each(payButtons, (element) => {
            element.removeEventListener('click', onClickPayButton)
            element.addEventListener('click', onClickPayButton)
        })
        _.each(deleteButtons, (element) => {
            element.removeEventListener('click', onClickDeleteButton)
            element.addEventListener('click', onClickDeleteButton)
        })
        _.each(deletePendingButtons, (element) => {
            element.removeEventListener('click', onClickDeletePendingButton)
            element.addEventListener('click', onClickDeletePendingButton)
        })
    }

    const completeCampaignSettings = () => {
        // Find first incomplete campaign (excluding drafts)
        const incompleteCampaign = data?.campaigns.find((campaign) => {
            return (
                campaign.userStatus === 'ACTIVE' &&
                campaign.systemStatus !== 'DRAFT' &&
                (campaign.landingPagesAggregate.count === 0 || campaign.platformsAggregate.count < 3)
            )
        })
        if (incompleteCampaign) {
            if (incompleteCampaign.platformsAggregate.count < 3) {
                navigate('/campaigns/platforms/' + incompleteCampaign.id)
            } else if (incompleteCampaign.landingPagesAggregate.count === 0) {
                navigate('/campaigns/' + incompleteCampaign.id + '/landing-pages')
            }
        }
    }

    useEffect(() => {
        if (location.state?.refetch) {
            setNotificationShow(false)
            refetch()
        }
    }, [location.state?.refetch, refetch])

    useEffect(() => {
        if (guide === 'on') {
            if (hasIncompleteCampaigns !== null && notificationShow) {
                if (hasIncompleteCampaigns && paidCampaigns && !guidedRef.current) {
                    showNotification(
                        <div>
                            <p>Great job! You have successfully created your campaign(s).</p>
                            <p>To complete their setup, please click the "Continue" button.</p>
                        </div>,
                        'info',
                        false,
                        'Continue',
                        () => completeCampaignSettings(),
                        false,
                    )
                    guidedRef.current = true
                }
                if (!hasIncompleteCampaigns && !guidedRef.current) {
                    // Exclude campaign with already completed media buying
                    const array = [...readyForMediaBuying].filter((item) => !item.live)
                    if (array.length > 0) {
                        const ready = array.some((element) => element.status)
                        if (ready) {
                            // Find first ready for media buying campaign
                            const campaign = array.find((campaign) => {
                                return campaign.status
                            })
                            showNotification(
                                <div>
                                    <p>Good news! Your campaign is ready to start media buying.</p>
                                    <p>To complete the media buying setup, please click the "Continue" button.</p>
                                </div>,
                                'info',
                                false,
                                'Continue',
                                () => {
                                    navigate(`/campaigns/media-buying/${campaign.id}/settings/ad-account`)
                                },
                                false,
                            )
                        } else {
                            showNotification(
                                <div>
                                    <p>Congratulations! You have successfully connected all your campaign(s).</p>
                                    <p>
                                        Please wait while your campaign(s) are prepared for media buying. The campaigns
                                        will be ready once the app aggregates sufficient data and identifies your target
                                        audience. This process may take up to 2-3 days.
                                    </p>
                                </div>,
                                'info',
                                false,
                                'Close',
                                null,
                                false,
                            )
                        }
                        guidedRef.current = true
                    }
                }
                if (!paidCampaigns && !guidedRefPaid.current) {
                    showNotification(
                        <div>
                            <p>You have payment issues with one or more of your campaigns.</p>
                            <p>
                                Please check your email. Once resolved, click the "Pay Invoice" button next to your
                                campaign in the table.
                            </p>
                        </div>,
                        'info',
                        false,
                        'Close',
                        null,
                        false,
                    )
                    guidedRefPaid.current = true
                }
            }
        }
    }, [hasIncompleteCampaigns, guidedRef, paidCampaigns, notificationShow, guide, readyForMediaBuying])

    if (loading) return <CubeLoader size="default" fullScreen={true} className="tw:min-h-[85vh] tw:max-h-[85vh]" />

    return (
        <Fragment>
            <Card className="page-card">
                <Card.Header className="d-flex justify-content-between">
                    <div className="header-title d-flex align-items-center justify-content-center">
                        <i className="icon text-gray-200 me-1">
                            <ListIcon size={24} color="currentColor" />
                        </i>
                        <h5 className="title mb-0 text-white">Licenses List</h5>
                        {hasDrafts ||
                            (true && (
                                <p className="mb-0">
                                    <span className="ms-2 badge rounded-pill bg-primary">Has Drafts</span>
                                </p>
                            ))}
                    </div>
                </Card.Header>
                <Card.Body className="p-0">
                    <div className="loader-container mt-4">
                        {payment.loading && (
                            <div className="loader-overlay">
                                <CubeLoader size="default" fullScreen={true} />
                            </div>
                        )}
                        <div className={`table-responsive ${payment.loading ? 'inactive' : ''}`}>
                            <DataTable
                                data={campaignData}
                                className="table-striped"
                                columns={columns}
                                onDraw={bindButtonEvents}
                                preserveInputOrder={true}
                                isRowDraggable={true}
                                getRowId={getCampaignRowId}
                                onRowReorder={handleCampaignReorder}
                                pageLength={50}
                            />
                        </div>
                    </div>
                </Card.Body>
            </Card>
            <Notification />
        </Fragment>
    )
})

CampaignList.displayName = 'CampaignList'
export default CampaignList
